import { useEffect, useRef } from 'react'
import { Activity, BatteryFull, CalendarDays, Leaf, LockKeyhole, Minus, Plus, Signal, Thermometer, Wifi } from 'lucide-react'
import { useI18n } from '../i18n.jsx'
import { ApiDiagram, PidDiagram, BuildingDiagram, TemperatureDiagram } from './SoftwareDiagrams.jsx'
import './software-algorithms.css'

function ControlInterface({ t }) {
  return <>
    <div className="software-temperature">
      <div className="software-temperature-readings"><span>{t('site.software_ui_temperature')}</span><span>{t('site.software_ui_set_temperature')}</span></div>
      <div className="software-temperature-controls"><Minus size={12} aria-hidden="true" /><div className="software-temperature-dial"><div><strong>22<span>℃</span></strong></div></div><Plus size={12} aria-hidden="true" /></div>
      <div className="software-ui-quick-controls">
        <div className="software-ui-quick-button"><BatteryFull size={24} aria-hidden="true" /><span>{t('site.software_ui_battery')}</span></div>
        <div className="software-ui-quick-button"><Leaf size={24} aria-hidden="true" /><span>{t('site.software_ui_comfort')}</span></div>
        <div className="software-ui-quick-button" title={t('site.software_ui_child_lock')}><LockKeyhole size={24} aria-hidden="true" /><span>{t('site.software_ui_locked')}</span></div>
      </div>
    </div>
    <div className="software-ui-block software-ui-schedule">
      <h4><CalendarDays size={17} aria-hidden="true" />{t('site.software_ui_schedule')}</h4>
      <p>{t('site.software_ui_weekdays')}</p>
      {[ ['06:30', '22°', true], ['09:00', '18°', false], ['17:30', '22°', true], ['22:00', '18°', false] ].map(([time, value, comfort]) => <div className="software-schedule-row" key={time}><time>{time}</time><span>{t(`site.software_ui_${comfort ? 'comfort' : 'eco'}`)}</span><strong>{value}</strong></div>)}
    </div>
    <div className="software-ui-block software-ui-sensors">
      <h4><Activity size={17} aria-hidden="true" />{t('site.software_ui_sensors')}</h4>
      <div className="software-sensor-value"><Thermometer size={17} aria-hidden="true" /><strong>21.8<span> °C</span></strong></div>
      <svg viewBox="0 0 240 94" fill="none" aria-hidden="true"><path d="M0 22h240M0 47h240M0 72h240" stroke="#d8e5ec" strokeWidth="1" /><path d="M0 70l20-12 20 6 20-17 20 9 20-24 20 7 20-16 20 7 20-10 20 7 20-16 20 7" stroke="#0082e5" strokeWidth="2" /><path d="M0 70C35 59 45 60 60 53S89 45 110 39 144 28 160 26 185 21 200 20 225 17 240 16" stroke="#76a9ee" strokeWidth="2" /></svg>
    </div>
  </>
}

export default function SoftwareAlgorithms() {
  const { t, language } = useI18n()
  const temperatureLabel = t('site.software_regulation_label')
  const section = useRef(null)
  const screen = useRef(null)
  const content = useRef(null)
  const pid = useRef(null)

  useEffect(() => {
    const root = section.current
    const viewport = screen.current
    const feed = content.current
    const phoneStage = viewport.closest('.software-phone-stage')
    const phoneArtwork = phoneStage.closest('.software-device-art')
    const phoneCard = phoneStage.closest('.software-device')
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    const mobileLayout = window.matchMedia('(max-width: 600px)')
    let visible = false
    let pidVisible = false
    let frame = 0
    let previous = 0
    let position = 0
    let direction = 1
    let hold = 1400
    let limit = 0
    let pixelScale = window.devicePixelRatio || 1
    const paintPosition = () => { feed.style.top = `${-Math.round(position * pixelScale) / pixelScale}px` }

    const tick = time => {
      const elapsed = previous ? Math.min(time - previous, 50) : 0
      previous = time
      if (hold > 0) hold -= elapsed
      else {
        position += direction * elapsed * .024
        if (position >= limit || position <= 0) {
          position = Math.max(0, Math.min(limit, position))
          direction *= -1
          hold = 1800
        }
        paintPosition()
      }
      frame = requestAnimationFrame(tick)
    }

    const synchronize = () => {
      cancelAnimationFrame(frame)
      previous = 0
      const running = visible && !document.hidden && !reducedMotion.matches && limit > 0
      root.dataset.motion = reducedMotion.matches ? 'reduced' : running ? 'running' : 'paused'
      root.dataset.pidMotion = reducedMotion.matches ? 'reduced' : pidVisible && !document.hidden ? 'running' : 'paused'
      if (running) frame = requestAnimationFrame(tick)
    }
    const measure = () => {
      const widthScale = Math.max(.1, (phoneStage.clientWidth - 2) / 210)
      phoneCard.style.setProperty('--software-phone-width-scale', widthScale.toFixed(4))
      const phoneHeight = parseFloat(getComputedStyle(phoneCard).getPropertyValue('--software-phone-height'))
      const scale = mobileLayout.matches ? widthScale : Math.max(.1, Math.min(widthScale, phoneArtwork.clientHeight / (phoneHeight + 10)))
      phoneCard.style.setProperty('--software-phone-scale', scale.toFixed(4))
      pixelScale = scale * (window.devicePixelRatio || 1)
      limit = Math.max(0, feed.scrollHeight - viewport.clientHeight)
      position = Math.min(position, limit)
      paintPosition()
      synchronize()
    }
    const intersection = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; synchronize() }, { threshold: .1 })
    const pidIntersection = new IntersectionObserver(([entry]) => { pidVisible = entry.isIntersecting; synchronize() }, { threshold: .15 })
    const resize = new ResizeObserver(measure)
    intersection.observe(viewport)
    pidIntersection.observe(pid.current.querySelector('.software-pid-control-curve'))
    resize.observe(viewport)
    resize.observe(feed)
    resize.observe(phoneStage)
    resize.observe(phoneArtwork)
    document.addEventListener('visibilitychange', synchronize)
    reducedMotion.addEventListener('change', synchronize)
    mobileLayout.addEventListener('change', measure)
    measure()
    return () => {
      cancelAnimationFrame(frame)
      intersection.disconnect()
      pidIntersection.disconnect()
      resize.disconnect()
      document.removeEventListener('visibilitychange', synchronize)
      reducedMotion.removeEventListener('change', synchronize)
      mobileLayout.removeEventListener('change', measure)
    }
  }, [language])

  useEffect(() => {
    const titles = [...section.current.querySelectorAll('.software-card-title')]
    const widths = new Map()
    let active = true
    const fitTitles = () => {
      for (const title of titles) {
        const width = title.clientWidth
        if (!width || widths.get(title) === width) continue
        widths.set(title, width)
        title.style.fontSize = ''
        title.style.whiteSpace = ''
        const style = getComputedStyle(title)
        const available = width - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
        const range = document.createRange()
        range.selectNodeContents(title)
        const base = parseFloat(style.fontSize)
        const wrapping = style.whiteSpace
        const singleLine = title.dataset.singleLine === 'true' && !window.matchMedia('(max-width: 600px)').matches
        let low = singleLine ? 1 : 16, high = base
        title.style.whiteSpace = 'nowrap'
        title.style.fontSize = `${base}px`
        if (range.getBoundingClientRect().width <= available) {
          title.style.whiteSpace = wrapping
          continue
        }
        // Inter's optical sizing changes glyph widths as the font size changes.
        for (let step = 0; step < 10; step++) {
          const size = (low + high) / 2
          title.style.fontSize = `${size}px`
          if (range.getBoundingClientRect().width <= available) low = size
          else high = size
        }
        title.style.fontSize = `${singleLine ? Math.floor(low) : Math.floor(low * 10) / 10}px`
        if (!singleLine && (wrapping === 'normal' || range.getBoundingClientRect().width > available)) title.style.whiteSpace = 'normal'
      }
    }
    const resize = new ResizeObserver(fitTitles)
    titles.forEach(title => resize.observe(title))
    fitTitles()
    document.fonts.ready.then(() => {
      if (active) { widths.clear(); fitTitles() }
    })
    return () => { active = false; resize.disconnect() }
  }, [language])

  return <section id="about" className="section software-section" ref={section} data-motion="paused" data-pid-motion="paused">
    <div className="section-inner">
      <div className="software-composition">
        <div className="software-first-column">
          <div className="software-heading"><h2>{t('site.software_title')}</h2><p>{t('site.software_description')}</p></div>
          <figure className="software-figure software-regulation"><div className="software-illustration"><TemperatureDiagram /></div><figcaption><span className="software-card-title" data-single-line="true">{temperatureLabel}</span></figcaption></figure>
          <figure className="software-figure software-building"><div className="software-illustration"><BuildingDiagram /></div><figcaption><span className="software-card-title">{t('site.software_building_label')}</span></figcaption></figure>
        </div>
        <figure className="software-figure software-pid-main">
          <div className="software-illustration" ref={pid}><PidDiagram labels={[t('site.software_pid_proportional'), t('site.software_pid_integral'), t('site.software_pid_derivative')]} /></div>
          <figcaption>
            <span className="software-card-title software-pid-title">{t('site.software_pid_label')}</span>
            <p className="software-pid-description">{t('site.software_pid_description')}</p>
          </figcaption>
        </figure>
        <figure className="software-figure software-api"><div className="software-illustration"><ApiDiagram /></div><figcaption><span className="software-card-title">{t('site.software_api_label')}</span></figcaption></figure>
        <figure className="software-device">
          <div className="software-illustration software-device-art">
            <div className="software-phone-stage">
              <div className="software-phone" role="img" aria-label={t('site.software_phone_alt')}>
                <div className="software-phone-display">
                <div className="software-phone-top" aria-hidden="true"><span>9:41</span><span className="software-phone-camera" /><span><Signal size={13} /><Wifi size={13} /><BatteryFull size={17} /></span></div>
                <div className="software-phone-screen" ref={screen}><div className="software-phone-feed" ref={content}><ControlInterface t={t} /></div></div>
                <div className="software-phone-bottom" aria-hidden="true"><span /></div>
                </div>
              </div>
            </div>
          </div>
          <figcaption>
            <span className="software-card-title">{t('site.software_app_label')}</span>
            <p className="software-app-description">{t('site.software_app_description')}</p>
          </figcaption>
        </figure>
      </div>
    </div>
  </section>
}
